/**
 * Вкладки охраны труда раздела «Качество и ОТ»: наряды-допуски (КЧ-05), инструктажи (КЧ-06), допуски работников
 * (КЧ-07), наблюдения за безопасностью (КЧ-09).
 */
import {
  BRIEFING_KINDS,
  BRIEFING_REASONS,
  type BriefingDetail,
  type BriefingKind,
  type BriefingReason,
  type BriefingSummary,
  CREDENTIAL_KINDS,
  type CredentialKind,
  type CredentialRow,
  OBSERVATION_CATEGORIES,
  type ObservationRow,
  PERMIT_KINDS,
  type PermitDetail,
  type PermitKind,
  type PermitSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { useProjectDefault, useProjectsWith } from '../psd/shared';
import { PermitSettingsDialog, usePermitSettings } from './PermitSettings';
import { PermitStatsPanel } from './PermitStats';
import {
  credentialLabel,
  credentialTone,
  localInput,
  localToIso,
  ObservationBadge,
  PermitBadge,
  usePermitPeople,
  useSafetyWorkers,
} from './safetyShared';

type S = { project?: string; status?: string };

const Row = ({
  children,
  to,
  params,
}: {
  children: React.ReactNode;
  to: string;
  params: Record<string, string>;
}) => (
  <Link
    to={to as never}
    params={params as never}
    className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-start md:gap-4"
  >
    {children}
  </Link>
);

const NewButton = ({ onClick, children }: { onClick: () => void; children: string }) => (
  <div className="mb-4">
    <Button variant="primary" size="lg" icon={<Plus className="size-4" />} onClick={onClick}>
      {children}
    </Button>
  </div>
);

/* ───────────────────────── Наряды-допуски ───────────────────────── */

export function PermitsList({ search }: { search: S }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const starters = useProjectsWith('qhse.edit');
  const q = useQuery({
    queryKey: ['qa-permits', search.project, search.status],
    queryFn: () =>
      api<PermitSummary[]>('/quality/permits', {
        query: {
          projectId: search.project,
          status: search.status === 'closed' ? 'closed' : search.status === 'all' ? 'all' : 'open',
        },
      }),
  });
  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        {starters.list.length ? (
          <Button
            variant="primary"
            size="lg"
            icon={<Plus className="size-4" />}
            onClick={() => setOpen(true)}
          >
            Новый наряд-допуск
          </Button>
        ) : null}
        <Button variant="secondary" size="lg" onClick={() => setSettings(true)}>
          Настройки нарядов
        </Button>
      </div>
      <PermitStatsPanel projectId={search.project} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title="Нарядов-допусков нет">
          Наряд готовит ОТК или ответственный за охрану труда: вид работ, срок, бригада, маршрут подписей.
          Работа без действующего наряда не должна открываться в плане дня.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((x) => (
            <li key={x.id}>
              <Row to="/qhse/permits/$permitId" params={{ permitId: x.id }}>
                <span className="num shrink-0 font-medium text-ink md:w-28">{x.number}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-ink [overflow-wrap:anywhere]">{x.title}</span>
                  <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {PERMIT_KINDS[x.kind].title} · {x.projectCode}
                    {x.place ? ` · ${x.place}` : ''} · ответственный {x.responsibleName} · в бригаде{' '}
                    {x.crewCount}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
                  <PermitBadge status={x.status} expired={x.expired} />
                  <span className="text-[13px] text-ink-2">до {dateTime(x.validUntil)}</span>
                </span>
              </Row>
            </li>
          ))}
        </ul>
      )}
      {open ? <NewPermitDialog projectId={search.project} onClose={() => setOpen(false)} /> : null}
      {settings ? <PermitSettingsDialog onClose={() => setSettings(false)} /> : null}
    </>
  );
}

function NewPermitDialog({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('qhse.edit');
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    kind: 'height' as PermitKind,
    title: '',
    place: '',
    objectId: '',
    contractorId: '',
    measures: '',
    responsibleId: '',
    approverId: '',
    issuerId: '',
    validFrom: localInput(0),
    validUntil: localInput(10),
  });
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, projectId, f.projectId, setProject);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const project = useQuery({ ...projectQuery(f.projectId), enabled: Boolean(f.projectId) });
  const cps = useQuery({ ...counterpartiesQuery('contractor'), retry: false });
  const people = usePermitPeople(f.projectId);
  const rules = usePermitSettings().data;
  const kinds = (Object.keys(PERMIT_KINDS) as PermitKind[]).filter((k) => rules?.kinds[k]?.enabled ?? true);
  // Вид работ, выключенный в настройках компании, не предлагается
  const kind = kinds.includes(f.kind) ? f.kind : (kinds[0] ?? f.kind);
  const needApprover = rules?.kinds[kind]?.approver ?? PERMIT_KINDS[kind].approver;
  const m = useMutation({
    mutationFn: () =>
      api<PermitDetail>('/quality/permits', {
        body: {
          projectId: f.projectId,
          kind,
          title: f.title.trim(),
          place: f.place.trim() || null,
          objectId: f.objectId || null,
          contractorId: f.contractorId || null,
          measures: f.measures.trim() || null,
          responsibleId: f.responsibleId,
          approverId: needApprover || f.approverId ? f.approverId || null : null,
          issuerId: f.issuerId,
          validFrom: localToIso(f.validFrom),
          validUntil: localToIso(f.validUntil),
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qa-permits'] });
      toast(`Наряд ${r.number} создан: поставьте бригаду`);
      onClose();
      navigate({ to: '/qhse/permits/$permitId', params: { permitId: r.id } });
    },
  });
  const sel = (k: 'responsibleId' | 'approverId' | 'issuerId', label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <Select className="h-11 md:h-10" value={f[k]} onChange={(e) => set(k, e.target.value)}>
        <option value="">Выберите</option>
        {(people.data ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.fullName}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Новый наряд-допуск"
      description={`Наряд выдаётся не больше чем на ${rules?.maxDays ?? 31} дн. Бригаду и маршрут подписей вы увидите на следующем шаге.`}
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={
              !f.projectId ||
              f.title.trim().length < 3 ||
              !f.responsibleId ||
              !f.issuerId ||
              (needApprover && !f.approverId)
            }
            onClick={() => m.mutate()}
          >
            Создать
          </Button>
        </>
      }
    >
      {m.isError ? <ErrorBox error={m.error} className="mb-3" /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект">
          <Select
            className="h-11 md:h-10"
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({
                ...x,
                projectId: e.target.value,
                objectId: '',
                responsibleId: '',
                approverId: '',
                issuerId: '',
              }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид работ">
          <Select
            className="h-11 md:h-10"
            value={kind}
            onChange={(e) => set('kind', e.target.value as PermitKind)}
          >
            {kinds.map((k) => (
              <option key={k} value={k}>
                {PERMIT_KINDS[k].title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Какие работы" className="sm:col-span-2">
          <Input className="h-11 md:h-10" value={f.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label="Объект">
          <Select
            className="h-11 md:h-10"
            value={f.objectId}
            onChange={(e) => set('objectId', e.target.value)}
          >
            <option value="">—</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code ? `${o.code} ` : ''}
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Место" hint="Оси, отметки">
          <Input className="h-11 md:h-10" value={f.place} onChange={(e) => set('place', e.target.value)} />
        </Field>
        <Field label="Подрядчик">
          <Select
            className="h-11 md:h-10"
            value={f.contractorId}
            onChange={(e) => set('contractorId', e.target.value)}
          >
            <option value="">Свои силы</option>
            {(cps.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <div />
        <Field label="Действует с">
          <Input
            className="h-11 md:h-10"
            type="datetime-local"
            value={f.validFrom}
            onChange={(e) => set('validFrom', e.target.value)}
          />
        </Field>
        <Field label="Действует до">
          <Input
            className="h-11 md:h-10"
            type="datetime-local"
            value={f.validUntil}
            onChange={(e) => set('validUntil', e.target.value)}
          />
        </Field>
        {sel('responsibleId', 'Ответственный руководитель работ', 'Подписывает первым')}
        {sel('issuerId', 'Выдаёт наряд', 'Подписывает последним; не тот же человек')}
        {needApprover || f.approverId
          ? sel(
              'approverId',
              'Согласует охрана труда',
              needApprover ? 'Для этого вида работ обязателен' : undefined,
            )
          : null}
        <Field label="Меры безопасности" className="sm:col-span-2">
          <Textarea rows={3} value={f.measures} onChange={(e) => set('measures', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Инструктажи ───────────────────────── */

export function BriefingsList({ search }: { search: S }) {
  const [open, setOpen] = useState(false);
  const starters = useProjectsWith('qhse.edit');
  const q = useQuery({
    queryKey: ['qa-briefings', search.project, search.status],
    queryFn: () =>
      api<BriefingSummary[]>('/quality/briefings', {
        query: {
          projectId: search.project,
          status: search.status === 'closed' ? 'closed' : search.status === 'all' ? 'all' : 'open',
        },
      }),
  });
  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        {starters.list.length ? (
          <Button
            variant="primary"
            size="lg"
            icon={<Plus className="size-4" />}
            onClick={() => setOpen(true)}
          >
            Оформить инструктаж
          </Button>
        ) : null}
        {search.project ? (
          <Link
            to="/qhse/briefings/journal"
            search={{ project: search.project } as never}
            className="inline-flex h-12 items-center rounded-md border border-line-strong px-5 text-[15px] font-medium text-ink hover:underline"
          >
            Журнал по форме
          </Link>
        ) : null}
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title="Инструктажей нет">
          Вводный, первичный, повторный, внеплановый и целевой. Каждый работник подписывает в приложении, если
          у него есть учётная запись, иначе проводивший отмечает бумажную подпись.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((x) => (
            <li key={x.id}>
              <Row to="/qhse/briefings/$briefingId" params={{ briefingId: x.id }}>
                <span className="num shrink-0 font-medium text-ink md:w-28">{x.number}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-ink [overflow-wrap:anywhere]">{x.topic}</span>
                  <span className="text-[13px] text-ink-2">
                    {BRIEFING_KINDS[x.kind]} · {x.projectCode} · {date(x.heldOn)} · проводил {x.conductorName}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
                  <Badge
                    tone={x.status === 'closed' ? 'neutral' : x.signed < x.attendees ? 'warning' : 'info'}
                  >
                    {x.status === 'closed' ? 'Журнал закрыт' : 'Идёт сбор подписей'}
                  </Badge>
                  <span className="text-[13px] text-ink-2">
                    подписей {x.signed} из {x.attendees}
                  </span>
                </span>
              </Row>
            </li>
          ))}
        </ul>
      )}
      {open ? <NewBriefingDialog projectId={search.project} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function NewBriefingDialog({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('qhse.edit');
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    kind: 'primary' as BriefingKind,
    reason: '' as BriefingReason | '',
    topic: '',
    place: '',
    heldOn: todayLocal(),
    conductorId: '',
    workerIds: [] as string[],
  });
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, projectId, f.projectId, setProject);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const people = usePermitPeople(f.projectId);
  const workers = useSafetyWorkers(f.projectId);
  const m = useMutation({
    mutationFn: () =>
      api<BriefingDetail>('/quality/briefings', {
        body: {
          projectId: f.projectId,
          kind: f.kind,
          reason: f.kind === 'unscheduled' ? f.reason || null : null,
          topic: f.topic.trim(),
          place: f.place.trim() || null,
          heldOn: f.heldOn,
          conductorId: f.conductorId,
          workerIds: f.workerIds,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qa-briefings'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast(`Инструктаж ${r.number} оформлен`);
      onClose();
      navigate({ to: '/qhse/briefings/$briefingId', params: { briefingId: r.id } });
    },
  });
  const toggle = (id: string) =>
    set('workerIds', f.workerIds.includes(id) ? f.workerIds.filter((x) => x !== id) : [...f.workerIds, id]);
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Оформить инструктаж"
      description="Оформляется после проведения. Подписи соберёте на следующем шаге."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={
              !f.projectId ||
              f.topic.trim().length < 3 ||
              !f.conductorId ||
              !f.workerIds.length ||
              (f.kind === 'unscheduled' && !f.reason)
            }
            onClick={() => m.mutate()}
          >
            Оформить
          </Button>
        </>
      }
    >
      {m.isError ? <ErrorBox error={m.error} className="mb-3" /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект">
          <Select
            className="h-11 md:h-10"
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({ ...x, projectId: e.target.value, conductorId: '', workerIds: [] }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид инструктажа">
          <Select
            className="h-11 md:h-10"
            value={f.kind}
            onChange={(e) => set('kind', e.target.value as BriefingKind)}
          >
            {(Object.keys(BRIEFING_KINDS) as BriefingKind[]).map((k) => (
              <option key={k} value={k}>
                {BRIEFING_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {f.kind === 'unscheduled' ? (
          <Field label="Основание" className="sm:col-span-2">
            <Select
              className="h-11 md:h-10"
              value={f.reason}
              onChange={(e) => set('reason', e.target.value as BriefingReason)}
            >
              <option value="">Выберите основание</option>
              {(Object.keys(BRIEFING_REASONS) as BriefingReason[]).map((k) => (
                <option key={k} value={k}>
                  {BRIEFING_REASONS[k]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Тема" className="sm:col-span-2">
          <Input className="h-11 md:h-10" value={f.topic} onChange={(e) => set('topic', e.target.value)} />
        </Field>
        <Field label="Дата проведения">
          <Input
            className="h-11 md:h-10"
            type="date"
            max={todayLocal()}
            value={f.heldOn}
            onChange={(e) => set('heldOn', e.target.value)}
          />
        </Field>
        <Field label="Проводил">
          <Select
            className="h-11 md:h-10"
            value={f.conductorId}
            onChange={(e) => set('conductorId', e.target.value)}
          >
            <option value="">Выберите</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={`Кого проинструктировали (${f.workerIds.length})`} className="sm:col-span-2">
          <div className="max-h-56 overflow-y-auto rounded-md border border-line-strong">
            {(workers.data ?? []).map((w) => (
              <label
                key={w.id}
                className="flex min-h-11 items-center gap-2 border-b border-line px-3 text-[14px] last:border-0"
              >
                <input
                  type="checkbox"
                  className="size-4"
                  checked={f.workerIds.includes(w.id)}
                  onChange={() => toggle(w.id)}
                />
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                  {w.fullName}
                  {w.counterpartyName ? ` · ${w.counterpartyName}` : ''}
                </span>
                {w.hasAccount ? <span className="text-[12px] text-ink-2">подпишет в приложении</span> : null}
              </label>
            ))}
            {workers.data && !workers.data.length ? (
              <p className="p-3 text-[13px] text-ink-2">
                Работников проекта нет: заведите их в разделе «Ресурсы», «Персонал».
              </p>
            ) : null}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Допуски работников ───────────────────────── */

export function CredentialsList({ search }: { search: S }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const editors = useProjectsWith('qhse.edit');
  const [problem, setProblem] = useState(true);
  const q = useQuery({
    queryKey: ['qa-credentials', search.project, problem],
    queryFn: () =>
      api<CredentialRow[]>('/quality/credentials', {
        query: { projectId: search.project, state: problem ? 'problem' : 'all' },
      }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/quality/credentials/${id}/revoke`, { body: { reason: 'Аннулирован вручную' } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa-credentials'] });
      toast('Допуск аннулирован');
    },
  });
  return (
    <>
      {editors.list.length ? <NewButton onClick={() => setOpen(true)}>Внести допуск</NewButton> : null}
      <label className="mb-3 flex min-h-11 items-center gap-2 text-[14px] text-ink">
        <input
          type="checkbox"
          checked={problem}
          onChange={(e) => setProblem(e.target.checked)}
          className="size-4"
        />
        Только истёкшие и истекающие в ближайшие 30 дней
      </label>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title={problem ? 'Просроченных допусков нет' : 'Допусков нет'}>
          Допуски и удостоверения работников: охрана труда, пожарная безопасность, электробезопасность,
          высота, стропальщик, сварщик, машинист. Без действующего допуска человека нельзя поставить в наряд.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((x) => (
            <li
              key={x.id}
              className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4 md:flex-row md:items-center md:gap-4"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-ink [overflow-wrap:anywhere]">{x.workerName}</span>
                <span className="block text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {CREDENTIAL_KINDS[x.kind as CredentialKind]}
                  {x.grade ? ` · ${x.grade}` : ''}
                  {x.number ? ` · № ${x.number}` : ''}
                  {x.counterpartyName ? ` · ${x.counterpartyName}` : ''}
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={credentialTone[x.state]}>{credentialLabel[x.state]}</Badge>
                <span className="text-[13px] text-ink-2">до {date(x.validUntil)}</span>
                {editors.list.length && x.state !== 'revoked' ? (
                  <button
                    type="button"
                    className="inline-flex min-h-11 items-center px-2 text-[13px] text-danger hover:underline md:min-h-0"
                    onClick={() =>
                      confirm('Аннулировать допуск? Человека нельзя будет ставить в наряды.') &&
                      revoke.mutate(x.id)
                    }
                  >
                    Аннулировать
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open ? <NewCredentialDialog projectId={search.project} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function NewCredentialDialog({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { list } = useProjectsWith('qhse.edit');
  const [project, setProject] = useState(
    projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
  );
  const setP = useCallback((id: string) => setProject(id), []);
  useProjectDefault(list, projectId, project, setP);
  const workers = useSafetyWorkers(project);
  const [f, setF] = useState({
    workerId: '',
    kind: 'height' as CredentialKind,
    grade: '',
    number: '',
    issuer: '',
    issuedOn: '',
    validUntil: '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const m = useMutation({
    mutationFn: () =>
      api('/quality/credentials', {
        body: {
          workerId: f.workerId,
          kind: f.kind,
          grade: f.grade.trim() || null,
          number: f.number.trim() || null,
          issuer: f.issuer.trim() || null,
          issuedOn: f.issuedOn || null,
          validUntil: f.validUntil,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa-credentials'] });
      qc.invalidateQueries({ queryKey: ['qa-safety-workers'] });
      toast('Допуск внесён');
      onClose();
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Внести допуск или удостоверение"
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.workerId || !f.validUntil}
            onClick={() => m.mutate()}
          >
            Внести
          </Button>
        </>
      }
    >
      {m.isError ? <ErrorBox error={m.error} className="mb-3" /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект работника">
          <Select
            className="h-11 md:h-10"
            value={project}
            onChange={(e) => {
              setProject(e.target.value);
              set('workerId', '');
            }}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Работник">
          <Select
            className="h-11 md:h-10"
            value={f.workerId}
            onChange={(e) => set('workerId', e.target.value)}
          >
            <option value="">Выберите</option>
            {(workers.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.fullName}
                {w.counterpartyName ? ` · ${w.counterpartyName}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид допуска">
          <Select
            className="h-11 md:h-10"
            value={f.kind}
            onChange={(e) => set('kind', e.target.value as CredentialKind)}
          >
            {(Object.keys(CREDENTIAL_KINDS) as CredentialKind[]).map((k) => (
              <option key={k} value={k}>
                {CREDENTIAL_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Группа, разряд" hint="«III группа», «5 разряд»">
          <Input className="h-11 md:h-10" value={f.grade} onChange={(e) => set('grade', e.target.value)} />
        </Field>
        <Field label="Номер">
          <Input className="h-11 md:h-10" value={f.number} onChange={(e) => set('number', e.target.value)} />
        </Field>
        <Field label="Кем выдан">
          <Input className="h-11 md:h-10" value={f.issuer} onChange={(e) => set('issuer', e.target.value)} />
        </Field>
        <Field label="Выдан">
          <Input
            className="h-11 md:h-10"
            type="date"
            value={f.issuedOn}
            onChange={(e) => set('issuedOn', e.target.value)}
          />
        </Field>
        <Field label="Действует до">
          <Input
            className="h-11 md:h-10"
            type="date"
            value={f.validUntil}
            onChange={(e) => set('validUntil', e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Наблюдения ───────────────────────── */

export function ObservationsList({ search }: { search: S }) {
  const q = useQuery({
    queryKey: ['qa-observations', search.project, search.status],
    queryFn: () =>
      api<ObservationRow[]>('/quality/observations', {
        query: {
          projectId: search.project,
          status:
            search.status === 'closed'
              ? 'closed'
              : search.status === 'overdue'
                ? 'overdue'
                : search.status === 'all'
                  ? 'all'
                  : 'open',
        },
      }),
  });
  return (
    <>
      <div className="mb-4">
        <Link
          to="/qhse/observe"
          search={{ project: search.project } as never}
          className="inline-flex h-12 items-center gap-2 rounded-md bg-accent px-5 text-[15px] font-semibold text-on-accent hover:underline"
        >
          <Plus className="size-4" />
          Сообщить об опасности
        </Link>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title="Наблюдений нет">
          Любой сотрудник может отправить фото опасной ситуации с телефона, при желании анонимно. Разбирает
          ОТК или ответственный за охрану труда.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((x) => (
            <li key={x.id}>
              <Row to="/qhse/observations/$observationId" params={{ observationId: x.id }}>
                <span className="num shrink-0 font-medium text-ink md:w-28">{x.number}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-ink [overflow-wrap:anywhere]">{x.description}</span>
                  <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {OBSERVATION_CATEGORIES[x.category]} · {x.projectCode}
                    {x.place ? ` · ${x.place}` : ''} ·{' '}
                    {x.anonymous ? 'анонимно' : (x.authorName ?? 'автор скрыт')} · {dateTime(x.createdAt)}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
                  <ObservationBadge status={x.status} overdue={x.overdue} />
                  {x.photos.length ? (
                    <span className="text-[13px] text-ink-2">фото {x.photos.length}</span>
                  ) : null}
                </span>
              </Row>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
